/**
 * Shared types/helpers for the admin-panel Consultations tab (Task #438,
 * Pharmacy Admin + Chain Admin). The `consultations` Firestore collection is
 * written by the CCS Pharmacy mobile app (see
 * artifacts/ccs-pharmacy/src/services/firebase/consultationSync.ts) once a
 * completed consultation's PDF is cloud-held — see firestore.rules for the
 * schema this module must stay in lockstep with.
 *
 * This is a deliberately lightweight, standalone label map — not a re-export
 * of the mobile app's `CCSCondition`/`CONDITIONS` (Expo-only module with
 * icon/color metadata the admin panels don't need).
 */

import type { DocumentData } from "firebase/firestore";
import {
  groupFormDataIntoSections,
  getFieldLabel,
  isCCSCondition,
  type ConsultationDetailSection,
  type ConsultationDetailRow,
} from "@workspace/consultation-forms";

export {
  groupFormDataIntoSections,
  getFieldLabel,
  isCCSCondition,
  type ConsultationDetailSection,
  type ConsultationDetailRow,
};

export const CONSULTATION_CONDITION_LABELS: Record<string, string> = {
  conjunctivitis_infective: "Acute Infective Conjunctivitis",
  rhinitis_allergic: "Allergic Rhinitis & Conjunctivitis",
  impetigo: "Impetigo",
  oral_thrush: "Oral Thrush",
  shingles: "Shingles",
  vv_thrush: "Vaginal Thrush",
  cold_sores: "Cold Sores",
  uti: "Urinary Tract Infection",
};

export function consultationConditionLabel(condition: string): string {
  return CONSULTATION_CONDITION_LABELS[condition] ?? condition;
}

/** Plaintext fields of a `consultations/{id}` doc — safe for list views. */
export interface ConsultationListRow {
  id: string;
  chainId: string;
  pharmacyId: string;
  pharmacistUid: string;
  pharmacistName: string;
  pharmacistPSINumber: string;
  condition: string;
  patientName: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string;
  syncedAt: Date | null;
}

/** Full `consultations/{id}` doc, including the encrypted detail blob. */
export interface ConsultationDoc extends ConsultationListRow {
  cloudPaths?: {
    pdf?: string;
    photos?: Record<string, string>;
  };
  encryptedDetail: string;
}

/** Decrypted shape of `encryptedDetail` once unwrapped with the chain key. */
export interface ConsultationDecryptedDetail {
  patientDOB?: string;
  patientAge?: number;
  formData?: Record<string, string>;
  // Matches the mobile app's `SignatureData` shape (see
  // artifacts/ccs-pharmacy/src/types/consultation.ts) as written by
  // consultationSync.ts. Fields are optional here because older records may
  // predate parts of the shape.
  signatures?: {
    patientSignature?: string | null;
    guardianSignature?: string | null;
    patientIsUnder16?: boolean;
    signedAt?: string | null;
  };
  imageAnalysis?: unknown;
  // Matches the mobile app's `SummaryBlock` shape (see
  // artifacts/ccs-pharmacy/src/types/consultation.ts) as actually written by
  // consultationSync.ts — NOT `{ heading, body }`, which this type used to
  // (incorrectly) claim and which rendered as empty boxes in both admin
  // panels since neither key ever existed on the real data.
  summaryBlocks?: Array<{ category?: string; label?: string; summary?: string } | string>;
}

function pickCloudPaths(data: DocumentData): ConsultationDoc["cloudPaths"] {
  const raw = data.cloudPaths as Record<string, unknown> | undefined;
  if (!raw || typeof raw !== "object") return undefined;
  return {
    pdf: typeof raw.pdf === "string" ? raw.pdf : undefined,
    photos:
      raw.photos && typeof raw.photos === "object"
        ? (raw.photos as Record<string, string>)
        : undefined,
  };
}

export function parseConsultationListRow(
  id: string,
  data: DocumentData,
): ConsultationListRow {
  return {
    id,
    chainId: String(data.chainId ?? ""),
    pharmacyId: String(data.pharmacyId ?? ""),
    pharmacistUid: String(data.pharmacistUid ?? ""),
    pharmacistName: String(data.pharmacistName ?? ""),
    pharmacistPSINumber: String(data.pharmacistPSINumber ?? ""),
    condition: String(data.condition ?? ""),
    patientName: String(data.patientName ?? ""),
    createdAt: String(data.createdAt ?? ""),
    updatedAt: String(data.updatedAt ?? ""),
    completedAt: String(data.completedAt ?? data.updatedAt ?? ""),
    syncedAt:
      typeof (data.syncedAt as { toDate?: () => Date } | undefined)?.toDate ===
      "function"
        ? (data.syncedAt as { toDate: () => Date }).toDate()
        : null,
  };
}

export function parseConsultationDoc(id: string, data: DocumentData): ConsultationDoc {
  return {
    ...parseConsultationListRow(id, data),
    cloudPaths: pickCloudPaths(data),
    encryptedDetail: String(data.encryptedDetail ?? ""),
  };
}

/**
 * Parses the decrypted JSON blob. Returns `null` (never throws) on malformed
 * JSON — callers should show a "detail unavailable" state rather than crash
 * the page, since this data came from client-side decryption.
 */
export function parseConsultationDetail(
  plaintextJson: string,
): ConsultationDecryptedDetail | null {
  try {
    const parsed = JSON.parse(plaintextJson);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as ConsultationDecryptedDetail;
  } catch {
    return null;
  }
}
